import type { MockedResponse } from "@apollo/client/testing";
import { PERSON_GRP_BENEFITS_QUERY } from "@/pages/people/[pgaId]/benefits/grp/queries";
import type { CommissionAllocationAgreementNode } from "../allocations/types";
import type { CommissionLineNode } from "../lines/types";
import { buildConnection, emptyPageInfo } from "./connection";
import { commissionCampaignFixtures, commissionEnrollmentFixtures } from "./fixtures";
import { createCatalogMock, withTypenames, type MockResolver } from "./mockUtils";
import { runtimeAllocationAgreements, runtimeCommissionLines, runtimeProgramConsents } from "./runtimeState";

const GRP_PROGRAM_SUMMARY = {
  id: "commission-program-grp",
  name: "Golf Retirement Plus",
  slug: "grp"
};

function resolvePerson(pgaId: string) {
  const enrollment = commissionEnrollmentFixtures.find((node) => node.person?.pgaId === pgaId);

  return (
    enrollment?.person ?? {
      id: `person-${pgaId}`,
      displayName: `Member ${pgaId}`,
      pgaId
    }
  );
}

function buildEnrollmentMockData(pgaId: string) {
  const enrollment = commissionEnrollmentFixtures.find((node) => node.person?.pgaId === pgaId);
  if (!enrollment) {
    return null;
  }

  return {
    __typename: "BackofficeCommissionEnrollment",
    id: enrollment.id,
    accountStatus: enrollment.accountStatus,
    program: withTypenames(enrollment.program, "BackofficeCommissionProgramSummary")
  };
}

function buildProgramConsentsMockData(crmPersonId: string) {
  const nodes = runtimeProgramConsents
    .filter((consent) => consent.crmPersonId === crmPersonId)
    .map((node) => withTypenames(node, "BackofficeCommissionProgramConsent"));

  return buildConnection("BackofficeCommissionProgramConsentConnection", nodes);
}

function toMockCampaign(campaignId: string) {
  const campaign = commissionCampaignFixtures.find((node) => node.id === campaignId);
  return {
    __typename: "BackofficeCommissionCampaign",
    id: campaignId,
    editionYear: campaign?.editionYear ?? 2026,
    status: campaign?.status ?? "ACTIVE",
    partner: withTypenames(
      { id: campaign?.partner.id ?? "commission-partner-unknown", name: campaign?.partner.name ?? "Unknown Sponsor" },
      "BackofficeCommissionPartner"
    )
  };
}

function toMockAgreement(agreement: CommissionAllocationAgreementNode) {
  return {
    __typename: "BackofficeCommissionAllocationAgreement",
    ...agreement,
    campaign: toMockCampaign(agreement.commissionCampaignId),
    splits: agreement.splits.map((split) => ({
      __typename: "BackofficeCommissionAllocationSplit",
      ...split
    }))
  };
}

function buildAgreementConnection(predicate: (node: CommissionAllocationAgreementNode) => boolean) {
  // All statuses, newest first, matching pga-backend's nested agreement connections.
  const nodes = runtimeAllocationAgreements
    .filter(predicate)
    .sort((a, b) => b.effectiveStart.localeCompare(a.effectiveStart))
    .map(toMockAgreement);

  return {
    __typename: "BackofficeCommissionAllocationAgreementConnection",
    nodes,
    totalCount: nodes.length,
    pageInfo: emptyPageInfo
  };
}

function withLineTypenames(line: CommissionLineNode) {
  return {
    __typename: "BackofficeCommissionLine",
    crmFacilityId: null,
    sourceRowReference: null,
    ingestBatch: null,
    allocationAgreement: null,
    ...line,
    person: withTypenames(line.person, "BackofficeCommissionEnrollmentPerson"),
    campaign: {
      __typename: "BackofficeCommissionCampaign",
      ...line.campaign,
      partner: withTypenames(line.campaign.partner, "BackofficeCommissionPartner"),
      program: withTypenames(line.campaign.program, "BackofficeCommissionProgramSummary")
    }
  };
}

function buildLinesMockData(crmPersonId: string) {
  const nodes = runtimeCommissionLines
    .filter((line) => line.person.id === crmPersonId)
    .slice(0, 100)
    .map(withLineTypenames);

  return buildConnection("BackofficeCommissionLineConnection", nodes);
}

function buildPaidOutTotalMockData(crmPersonId: string, from: string, to: string) {
  const inRange = runtimeCommissionLines.filter((line) => {
    if (line.person.id !== crmPersonId || !line.paidOutAt) return false;
    const paidDate = line.paidOutAt.slice(0, 10);
    return paidDate >= from && paidDate <= to;
  });

  return {
    __typename: "BackofficeCommissionPaidOutTotal",
    amountCents: inRange.reduce((sum, line) => sum + line.amountCents, 0),
    lineCount: inRange.length,
    from,
    to
  };
}

export function buildPersonGrpBenefitsMockData(variables: Record<string, unknown>) {
  const pgaId = String(variables.pgaId ?? "");
  const paidOutFrom = String(variables.paidOutFrom ?? "2026-01-01");
  const paidOutTo = String(variables.paidOutTo ?? "2026-12-31");
  const person = resolvePerson(pgaId);

  return {
    person: {
      __typename: "CRMPerson",
      id: person.id,
      displayName: person.displayName,
      pgaId,
      commissionBenefitProgram: {
        __typename: "BackofficeCommissionProgramParticipation",
        program: withTypenames(GRP_PROGRAM_SUMMARY, "BackofficeCommissionProgramSummary"),
        enrollment: buildEnrollmentMockData(pgaId),
        programConsents: buildProgramConsentsMockData(person.id),
        allocationAgreements: buildAgreementConnection((node) => node.receiverCrmPersonId === person.id),
        receivedAllocationAgreements: buildAgreementConnection(
          (node) =>
            node.receiverCrmPersonId !== person.id && node.splits.some((split) => split.crmPersonId === person.id)
        ),
        lines: buildLinesMockData(person.id),
        paidOutTotal: buildPaidOutTotalMockData(person.id, paidOutFrom, paidOutTo)
      }
    }
  };
}

/** Add Authorization(s) wizard data: the member, their FAAs, and the IAAs they share. */
export function buildPersonGrpIaaSetupMockData(variables: Record<string, unknown>) {
  const pgaId = String(variables.pgaId ?? "");
  const person = resolvePerson(pgaId);
  return {
    person: {
      __typename: "CRMPerson",
      id: person.id,
      displayName: person.displayName,
      pgaId,
      primaryFacility: null,
      commissionBenefitProgram: {
        __typename: "BackofficeCommissionProgramParticipation",
        programConsents: buildProgramConsentsMockData(person.id),
        allocationAgreements: buildAgreementConnection((node) => node.receiverCrmPersonId === person.id)
      }
    }
  };
}

export const personGrpBenefitsMockData = buildPersonGrpBenefitsMockData({
  pgaId: "12345678",
  paidOutFrom: "2026-01-01",
  paidOutTo: "2026-12-31"
});

export const personGrpBenefitsApolloMock = createCatalogMock(PERSON_GRP_BENEFITS_QUERY, personGrpBenefitsMockData);

const adaLiveAgreement = personGrpBenefitsMockData.person.commissionBenefitProgram.allocationAgreements.nodes[0]!;

/** Ada's benefits plus an IAA where Grace Hopper is the receiver and Ada holds a 25% split. */
export const personGrpReceivedBenefitsMockData = {
  ...personGrpBenefitsMockData,
  person: {
    ...personGrpBenefitsMockData.person,
    commissionBenefitProgram: {
      ...personGrpBenefitsMockData.person.commissionBenefitProgram,
      receivedAllocationAgreements: {
        __typename: "BackofficeCommissionAllocationAgreementConnection",
        pageInfo: { hasNextPage: false, endCursor: null },
        nodes: [
          {
            ...adaLiveAgreement,
            id: "commission-iaa-grace-2026",
            receiverCrmPersonId: "person-grace",
            facilityName: "Pebble Creek",
            effectiveStart: "2026-02-01T00:00:00Z",
            effectiveEnd: "2026-12-31T00:00:00Z",
            splits: [
              {
                ...adaLiveAgreement.splits[0]!,
                id: "commission-split-grace",
                crmPersonId: "person-grace",
                pgaId: "55555555",
                displayName: "Grace Hopper",
                splitPercent: 75
              },
              { ...adaLiveAgreement.splits[0]!, id: "commission-split-ada-received", splitPercent: 25 }
            ]
          }
        ],
        totalCount: 1
      }
    }
  }
};

export const personGrpReceivedBenefitsApolloMock = createCatalogMock(
  PERSON_GRP_BENEFITS_QUERY,
  personGrpReceivedBenefitsMockData
);

export const personGrpBenefitsApolloMocks: MockedResponse[] = [personGrpBenefitsApolloMock];

export const personGrpBenefitsMockResults: Record<string, Record<string, unknown>> = {
  PersonGrpBenefits: personGrpBenefitsMockData
};

/** Data for the person GRP IAA page: the page's member plus the IAA with that id, when one exists. */
export function buildPersonGrpAllocationAgreementMockData(variables: Record<string, unknown>) {
  const pgaId = String(variables.pgaId ?? "");
  const person = resolvePerson(pgaId);
  const agreement = runtimeAllocationAgreements.find((node) => node.id === String(variables.iaaId ?? ""));

  return {
    person: { __typename: "CRMPerson", id: person.id, displayName: person.displayName, pgaId },
    commissionAllocationAgreementById: agreement ? toMockAgreement(agreement) : null
  };
}

export const personGrpBenefitsVariableMockResolvers: Record<string, MockResolver> = {
  PersonGrpBenefits: (variables) => buildPersonGrpBenefitsMockData(variables),
  PersonGrpAllocationAgreement: (variables) => buildPersonGrpAllocationAgreementMockData(variables),
  PersonGrpIaaSetup: (variables) => buildPersonGrpIaaSetupMockData(variables)
};
